import React, { FC } from 'react';
import { Control } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { ChevronDownIcon } from '../../../../assets/icons/index';
import { AppFormattedMessage } from '../../../../components/app-formatted-message/index';
import Button from '../../../../components/button/index';
import {
  ComboboxButton,
  ComboboxOptions,
  FormCombobox,
  FormComboboxInput,
  FormComboboxOption,
} from '../../../../components/combobox/index';
import { Tooltip } from '../../../../components/tooltip/index';
import { ShareClass as ShareClassHook } from '../../../../hooks/index';
import { ShareClass } from '../../../../types/share-classes.types';
import { FormSchema } from '../event-form.validation';

export type ShareClassValue = {
  conversionRatio: string;
  id: string;
  name: string;
};

export type ShareClassesItemsListProps = {
  control: Control<FormSchema>;
  data: ShareClass[];
  fieldName: string;
  isLoading: boolean;
};

const ShareClassesItemsList: FC<ShareClassesItemsListProps> = ({
  data,
  control,
  isLoading,
  fieldName,
}) => {
  const navigate = useNavigate();
  if (!data || isLoading)
    return (
      <span>
        <AppFormattedMessage id="LOADING" />
      </span>
    );
  return (
    <>
      {data.length > 0 ? (
        <>
          {data.map(({ name, id, conversionRatio }, i) => (
            <FormComboboxOption
              control={control}
              key={`${name}_${i}`}
              name={fieldName as keyof FormSchema}
              value={{ id, name, conversionRatio } as unknown as FormSchema[keyof FormSchema]}
            >
              <Tooltip content={name}>
                <span className="text-sm text-gray-700">{name}</span>
              </Tooltip>
            </FormComboboxOption>
          ))}
        </>
      ) : (
        <span className="ml-2 px-2 py-[6px] text-sm font-normal text-gray-700">
          <AppFormattedMessage id="NO_RESULTS_FOUND" />
        </span>
      )}

      <Button
        className="h-[37px] w-full justify-start rounded bg-brand-25 px-[6px] text-start text-sm font-[450] text-brand-700"
        onClick={() => navigate('/share-classes?openAddModal=true')}
        styleType="NONE"
      >
        + <AppFormattedMessage id="ADD_NEW_SHARE_CLASS" />
      </Button>
    </>
  );
};

export type ShareClassComboboxProps = {
  commonStockIsAllowed?: boolean;
  companyId: string;
  control: Control<FormSchema>;
  fieldName: string;
  shareClassFromId?: string;
  stakeholderFromId?: string;
};

export const ShareClassCombobox: FC<ShareClassComboboxProps> = ({
  control,
  companyId,
  fieldName,
  commonStockIsAllowed = true,
  shareClassFromId,
  stakeholderFromId,
}) => {
  const { shareClasses, isLoading } = ShareClassHook.useShareClasses({ companyId, filter: 'all' });

  if (!shareClasses) return;

  const filteredShareClasses =
    !commonStockIsAllowed && stakeholderFromId
      ? shareClasses.filter(
          ({ name, stakeholderIds }) =>
            name !== 'Common Stock' &&
            stakeholderIds &&
            stakeholderIds?.includes(stakeholderFromId),
        )
      : shareClassFromId
        ? shareClasses.filter(({ id }) => id !== shareClassFromId)
        : stakeholderFromId
          ? shareClasses.filter(
              ({ stakeholderIds }) => stakeholderIds && stakeholderIds?.includes(stakeholderFromId),
            )
          : shareClasses;

  return (
    <FormCombobox
      className="relative w-[224px]"
      control={control}
      name={fieldName as keyof FormSchema}
    >
      <div className="relative">
        <FormComboboxInput
          className={'bg-gray-900'}
          control={control}
          customValue={(value) => (value as ShareClassValue)?.name}
          icon={<ChevronDownIcon className="mt-2" />}
          name={fieldName as keyof FormSchema}
          placeholder={<AppFormattedMessage id="SHARE_CLASS" />}
          readOnly
        />
        <ComboboxButton className="absolute left-0 top-0 z-1 h-full w-full" />
      </div>
      <ComboboxOptions className="w-[224px]">
        <ShareClassesItemsList
          control={control}
          data={filteredShareClasses || []}
          fieldName={fieldName}
          isLoading={isLoading}
        />
      </ComboboxOptions>
    </FormCombobox>
  );
};
